import { useEffect, useMemo, useState } from 'react';
import { CHECKS, ELEMENT_IDS, REDIRECT_HOPS, SAMPLE_MP4 } from './webview-test/checks';
import { getEngine } from './webview-test/runtime';
import type { CheckDef, Result, Status } from './webview-test/types';
import { useBranding } from '@/hooks/useBranding';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';

function Chip({ status }: { status: Status }) {
  const map: Record<Status, { text: string; cls: string }> = {
    pending: { text: '—', cls: 'bg-surface-muted text-content-subtle' },
    running: { text: '…', cls: 'bg-surface-muted text-content-subtle' },
    pass: { text: 'OK', cls: 'bg-success-soft text-success' },
    fail: { text: 'FAIL', cls: 'bg-danger-soft text-danger' },
    skip: { text: 'N/A', cls: 'bg-warning-soft text-warning' },
  };
  const m = map[status];
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${m.cls}`}>
      {m.text}
    </span>
  );
}

/**
 * One checklist row. The data-* attributes are part of the machine contract:
 * the Espresso/UiAutomator suite locates rows, buttons and details by them and
 * waits on data-status.
 */
function Row({
  def,
  result,
  action,
  children,
}: {
  def: CheckDef;
  result: Result;
  action?: { label: string; onClick: () => void };
  children?: React.ReactNode;
}) {
  return (
    <li className="px-4 py-3" data-check={def.id} data-bucket={def.bucket} data-status={result.status}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-content">{def.title}</span>
            <Chip status={result.status} />
          </div>
          {def.desc && <div className="text-xs text-content-subtle mt-0.5">{def.desc}</div>}
          <div
            data-check-detail={def.id}
            className={`mt-1 text-xs text-content-muted break-all font-mono${result.detail ? '' : ' hidden'}`}
          >
            {result.detail}
          </div>
          {children && <div className="mt-2">{children}</div>}
        </div>
        {action && (
          <button
            type="button"
            data-check-run={def.id}
            onClick={action.onClick}
            className="shrink-0 text-xs px-3 py-1.5 rounded border border-border-strong bg-surface hover:bg-surface-muted"
          >
            {action.label}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * The one sub-render this route still serves to its own checks: the
 * postMessage echo target. The iframe and popup fixtures are static documents
 * under /checklist/ so they answer without booting React first.
 */
function specialMode(): JSX.Element | null {
  const qs = new URLSearchParams(window.location.search);
  if (qs.get('pm') === '1') {
    window.onmessage = (e) => {
      try { (window.parent || window.top)?.postMessage(e.data, '*'); } catch { /* gone */ }
    };
    return <div style={{ padding: 16, fontFamily: 'system-ui' }}>pm echo ready</div>;
  }
  return null;
}

export function WebviewTest() {
  const mode = specialMode();
  return mode ?? <Checklist />;
}

function Checklist() {
  const { name: brand } = useBranding();
  const engine = useMemo(getEngine, []);
  const [results, setResults] = useState(() => engine.results());

  useEffect(() => engine.subscribe(() => setResults(engine.results())), [engine]);

  // ?runner=1 drives the page from code: set the harness up and, unless the
  // caller asked not to, run every headless check as soon as the rows exist.
  useEffect(() => {
    if (!engine.params.runner || !engine.params.autorun || engine.resumedThisLoad) return;
    void engine.runAuto();
  }, [engine]);

  const runUi = (def: CheckDef) => {
    void engine.runFromUi(def.id);
  };

  const runRedirectNav = (mode: 'http' | 'meta' | 'js' | 'mixed') => {
    void engine.runFromUi('redirect-navigation', (ctx) => {
      ctx.leave(
        `${API_BASE}/checklist/redirect/${REDIRECT_HOPS}?mode=${mode}&dest=page&ts=${Date.now()}`
          + (ctx.runner ? '&runner=1' : ''),
        { mode, hops: String(REDIRECT_HOPS) },
      );
      return new Promise(() => {});
    });
  };

  const onFilePick = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const f = e.target.files?.[0];
    void engine.runFromUi(id, async () =>
      f
        ? { status: 'pass', detail: `${f.name} · ${f.type || 'no mime'} · ${f.size} bytes` }
        : { status: 'fail', detail: 'no file' },
    );
  };

  // Row extras: the media elements, iframes and inputs the checks drive. They
  // carry the stable ids the check functions look up.
  const extras: Record<string, React.ReactNode> = {
    camera: <video id={ELEMENT_IDS.camera} playsInline muted className="w-40 h-28 bg-black rounded" />,
    'file-input-capture': (
      <input
        id={ELEMENT_IDS.fileCapture}
        data-check-input="file-input-capture"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => onFilePick(e, 'file-input-capture')}
        className="text-xs"
      />
    ),
    'file-input-generic': (
      <input
        id={ELEMENT_IDS.fileGeneric}
        data-check-input="file-input-generic"
        type="file"
        onChange={(e) => onFilePick(e, 'file-input-generic')}
        className="text-xs"
      />
    ),
    'iframe-same-origin': (
      <iframe
        id={ELEMENT_IDS.iframeSameOrigin}
        title="iframe-same-origin"
        className="w-full h-24 border border-border rounded"
      />
    ),
    'iframe-cross-origin': (
      <iframe
        id={ELEMENT_IDS.iframeCrossOrigin}
        title="iframe-cross-origin"
        className="w-full h-24 border border-border rounded"
      />
    ),
    'audio-autoplay': (
      /* Short tone (public sample) */
      <audio
        id={ELEMENT_IDS.audioAutoplay}
        src="https://actions.google.com/sounds/v1/cartoon/clang_and_wobble.ogg"
        preload="auto"
      />
    ),
    'video-inline-mp4': (
      <video
        id={ELEMENT_IDS.videoMp4}
        playsInline
        muted
        preload="auto"
        src={SAMPLE_MP4}
        className="w-40 h-24 bg-black rounded"
      />
    ),
    'hls-native': (
      <video
        id={ELEMENT_IDS.videoHls}
        playsInline
        muted
        preload="metadata"
        src="https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8"
        className="w-40 h-24 bg-black rounded"
      />
    ),
    'redirect-navigation': (
      <div className="flex flex-wrap gap-2">
        {(['http', 'meta', 'js', 'mixed'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => runRedirectNav(m)}
            className="text-xs px-3 py-1.5 rounded border border-border-strong bg-surface hover:bg-surface-muted"
          >
            {m === 'http' ? '302' : m}
          </button>
        ))}
      </div>
    ),
    'postmessage-iframe': (
      <iframe
        id={ELEMENT_IDS.iframePostMessage}
        title="postmessage-iframe"
        className="w-full h-16 border border-border rounded"
      />
    ),
  };

  const env = useEnv();

  return (
    <div className="min-h-screen bg-surface-sunken py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-content">
            <span className="text-primary">{brand}</span> Webview Test
          </h1>
          <p className="mt-1 text-sm text-content-muted">
            Open this page inside the app's webview and run each check. Useful for verifying that
            casino-side features (camera, popups, iframes, cookies, downloads, deep links) work in
            production wrappers.
          </p>
        </div>

        <div className="bg-surface border border-border rounded-lg overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-border text-sm font-medium text-content-muted">Environment</div>
          <div className="px-4 py-3 text-xs font-mono text-content-muted space-y-1 break-all">
            <div>UA: {env.ua}</div>
            <div>Viewport: {env.vw}×{env.vh}</div>
            <div>Secure context: {String(env.secure)}</div>
            <div>navigator.cookieEnabled: {String(env.cookieEnabled)}</div>
            <div>navigator.onLine: {String(env.online)}</div>
            <div>Languages: {env.languages}</div>
            <div>Timezone: {env.tz}</div>
            <div>Connection: {env.connection}</div>
            <div>CPU cores: {String(env.cores)} · Device memory: {env.deviceMemory}</div>
            <div>ServiceWorker: {String(env.serviceWorker)} · PushManager: {String(env.pushManager)} · Cache API: {String(env.cacheApi)}</div>
            <div>WakeLock: {String(env.wakeLock)} · WebShare: {String(env.webShare)} · WebAssembly: {String(env.wasm)}</div>
            <div>Native bridge: {env.bridge}</div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <ul className="divide-y divide-border">
            {CHECKS.map((def) => (
              <Row
                key={def.id}
                def={def}
                result={results[def.id]}
                action={{ label: 'Test', onClick: () => runUi(def) }}
              >
                {extras[def.id]}
              </Row>
            ))}
          </ul>
        </div>

        <p className="mt-4 text-xs text-content-subtle text-center">
          This page is public — no login required.
        </p>
      </div>
    </div>
  );
}

interface EnvInfo {
  ua: string; vw: number; vh: number; cookieEnabled: boolean; online: boolean; secure: boolean;
  languages: string; tz: string; connection: string; cores: number | string; deviceMemory: string;
  serviceWorker: boolean; pushManager: boolean; cacheApi: boolean; wakeLock: boolean; webShare: boolean;
  wasm: boolean; bridge: string;
}

function useEnv(): EnvInfo {
  const [env, setEnv] = useState<EnvInfo>(() => {
    const nav = navigator as any;
    const bridges: string[] = [];
    if ('AndroidBridge' in window || 'Android' in window) bridges.push('Android');
    if ((window as any).webkit?.messageHandlers) bridges.push('WKWebView (webkit.messageHandlers)');
    if ('ReactNativeWebView' in window) bridges.push('ReactNativeWebView');
    if ('flutter_inappwebview' in window) bridges.push('flutter_inappwebview');
    return {
      ua: navigator.userAgent,
      vw: window.innerWidth,
      vh: window.innerHeight,
      cookieEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      secure: window.isSecureContext,
      languages: (navigator.languages || [navigator.language]).join(', '),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection: nav.connection ? `${nav.connection.effectiveType ?? '?'} (downlink=${nav.connection.downlink ?? '?'}, rtt=${nav.connection.rtt ?? '?'})` : 'n/a',
      cores: navigator.hardwareConcurrency ?? 'n/a',
      deviceMemory: nav.deviceMemory ? `${nav.deviceMemory} GB` : 'n/a',
      serviceWorker: 'serviceWorker' in navigator,
      pushManager: 'PushManager' in window,
      cacheApi: 'caches' in window,
      wakeLock: !!(nav.wakeLock && typeof nav.wakeLock.request === 'function'),
      webShare: typeof (navigator as any).share === 'function',
      wasm: typeof WebAssembly === 'object' && typeof WebAssembly.validate === 'function',
      bridge: bridges.length ? bridges.join(', ') : 'none detected',
    };
  });

  useEffect(() => {
    const update = () =>
      setEnv((e) => ({ ...e, vw: window.innerWidth, vh: window.innerHeight, online: navigator.onLine }));
    window.addEventListener('resize', update);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return env;
}
